import deepEqual from 'fast-deep-equal'
import { useState } from 'react'

import { type LanguageKey } from '@/domain/locale'
import { useIsomorphicLayoutEffect } from '@/hooks/useIsomorphicLayoutEffect'
import { GOT_FALLBACKS } from '@/lib/ads/constants/gotFallbacks'
import {
  type AdConfig,
  type GotApiResponse,
  type GotDynamicConditions,
  type srpGpt,
} from '@/lib/ads/types/adConfig'
import {
  flattenGptArray,
  getFallbackKey,
  processGotAdSettings,
} from '@/lib/ads/utils/globalOptimizationTool'
import { isJSON } from '@/types/common'
import sendToLogger from '@/utils/sendToLogger'
/**
 * Fetches display ad settings from GOT API and
 * transforms response into format suitable for
 * passing into the AdvertisingProvider component
 * @param gotEndpoint endpoint to use for GOT call
 */
export const useFetchAdConfig = (
  gotEndpoint: string,
  gptTargetingFromProps: srpGpt[],
  languageKey: LanguageKey
) => {
  const [gptConfig, setGptConfig] = useState<AdConfig>()

  const [gptTargeting, setGptTargeting] = useState<srpGpt[]>(gptTargetingFromProps)

  /**
   * We need to update the gptConfig if any of the gptTargeting values change.
   * Also need to use useLayoutEffect client-side so that the effects run
   * synchronously to prevent issues with stale GPT targeting values.
   */
  useIsomorphicLayoutEffect(() => {
    const isEqual = deepEqual(gptTargeting, gptTargetingFromProps)

    if (!isEqual) {
      setGptTargeting(gptTargetingFromProps)
    }
  }, [gptTargetingFromProps, gptTargeting])

  useIsomorphicLayoutEffect(() => {
    let ignore = false
    const dynamicConditions: GotDynamicConditions = {
      lang: languageKey,
      mode: 'light',
    }

    const fetchGOT = async () => {
      try {
        const gotUrl = `/got-api/${gotEndpoint}`

        const response = await fetch(gotUrl, {
          method: 'POST',
          mode: 'cors',
          body: JSON.stringify({ dynamicConditions }),
          headers: { 'Content-Type': 'application/json' },
        })

        /**
         * The "response" sometimes returns as an HTML and .json() is not catching the error
         * We will accept the response as text and parse it to JSON if it is of type json
         */
        const textData = await response.text()
        if (!isJSON(textData)) {
          throw new Error(`Invalid JSON in GOT response`)
        }

        const data: GotApiResponse = JSON.parse(textData)

        const adSettings = data.adSettings

        // an invalid category id will return an empty array
        if (!adSettings.length) {
          throw new Error(`No adSettings found in GOT response`)
        }

        if (!ignore) {
          setGptConfig(processGotAdSettings(adSettings, flattenGptArray(gptTargeting)))
        }
      } catch (err) {
        sendToLogger(
          err,
          {
            tags: {
              fn: 'fetchGOT',
              component: 'useFetchAdConfig',
              gotEndpoint,
              languageKey,
            },
          },
          'info'
        )

        const fallbackKey = getFallbackKey(gotEndpoint)
        const fallback = GOT_FALLBACKS[fallbackKey]

        if (!fallback) {
          /** if can't find a fallback function based on the keys  */
          sendToLogger(`fetchGOT - invalid fallback - fallbackKey: ${fallbackKey}`, {
            tags: { fn: 'getFallbackKey' },
          })
        }

        const adSettings = fallback?.(languageKey).adSettings || []

        setGptConfig(processGotAdSettings(adSettings, flattenGptArray(gptTargeting)))
      }
    }
    fetchGOT()

    // clean up fn ensures that all responses except the last requested one will be ignored
    // https://beta.reactjs.org/learn/synchronizing-with-effects#fetching-data
    return () => {
      ignore = true
    }
  }, [gotEndpoint, languageKey, gptTargeting])

  return { gptConfig }
}
